/**
 * @fileoverview Atlas API client
 * @module Atlas/lib/atlasApi
 *
 * Provides API-based data fetching for Atlas content when
 * the atlas_backend feature flag is enabled. Falls back to
 * bundled JSON when the flag is disabled or API is unavailable.
 *
 * Usage with TanStack Query:
 * ```
 * import { useQuery } from '@tanstack/react-query';
 * import { fetchProducts } from './atlasApi';
 *
 * function ProductList() {
 *   const { data, isLoading, error } = useQuery({
 *     queryKey: ['atlas', 'products'],
 *     queryFn: fetchProducts,
 *   });
 *   ...
 * }
 * ```
 */

import apiClient from '../../../lib/apiClient';
import * as contentLoader from './contentLoader';

// Feature flag check - looks for atlas_backend in user's feature flags
// Falls back to bundled JSON if not enabled
function isApiEnabled() {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return false;

  // Check localStorage for feature flag override (dev mode)
  const override = localStorage.getItem('atlas_api_enabled');
  if (override !== null) {
    return override === 'true';
  }

  // Default to false - MVP uses bundled JSON
  // Set to true when atlas_backend feature flag is enabled
  return false;
}

/**
 * Fetch products from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.pillar] - Filter by pillar
 * @param {string} [filters.maturity] - Filter by maturity
 * @param {string} [filters.capability] - Filter by capability
 * @param {string} [filters.search] - Search query
 * @returns {Promise<import('./types').Product[]>}
 */
export async function fetchProducts(filters = {}) {
  if (!isApiEnabled()) {
    // Fall back to bundled JSON
    let products = contentLoader.getProducts();

    // Apply filters locally
    if (filters.pillar) {
      products = products.filter(p => p.pillar === filters.pillar);
    }
    if (filters.maturity) {
      products = products.filter(p => p.maturity === filters.maturity);
    }
    if (filters.capability) {
      products = products.filter(p => p.capabilities?.includes(filters.capability));
    }
    if (filters.search) {
      const q = filters.search.toLowerCase();
      products = products.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }

    return products;
  }

  try {
    const params = new URLSearchParams();
    if (filters.pillar) params.append('pillar', filters.pillar);
    if (filters.maturity) params.append('maturity', filters.maturity);
    if (filters.capability) params.append('capability', filters.capability);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/api/atlas/products${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.products || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getProducts();
  }
}

/**
 * Fetch a single product by ID
 * @param {string} id - Product ID
 * @returns {Promise<import('./types').Product|null>}
 */
export async function fetchProduct(id) {
  if (!isApiEnabled()) {
    return contentLoader.getProductById(id) || null;
  }

  try {
    const product = await apiClient.get(`/api/atlas/products/${id}`);
    return product;
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getProductById(id) || null;
  }
}

/**
 * Fetch capabilities from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.pillar] - Filter by pillar
 * @returns {Promise<import('./types').Capability[]>}
 */
export async function fetchCapabilities(filters = {}) {
  if (!isApiEnabled()) {
    let capabilities = contentLoader.getCapabilities();
    if (filters.pillar) {
      capabilities = capabilities.filter(c => c.pillar === filters.pillar);
    }
    return capabilities;
  }

  try {
    const params = new URLSearchParams();
    if (filters.pillar) params.append('pillar', filters.pillar);

    const queryString = params.toString();
    const url = `/api/atlas/capabilities${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.capabilities || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getCapabilities();
  }
}

/**
 * Fetch frameworks from API or fall back to JSON
 * @returns {Promise<import('./types').Framework[]>}
 */
export async function fetchFrameworks() {
  if (!isApiEnabled()) {
    return contentLoader.getFrameworks();
  }

  try {
    const response = await apiClient.get('/api/atlas/frameworks');
    return response.frameworks || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getFrameworks();
  }
}

/**
 * Fetch a single framework by ID
 * @param {string} id - Framework ID
 * @returns {Promise<import('./types').Framework|null>}
 */
export async function fetchFramework(id) {
  if (!isApiEnabled()) {
    return contentLoader.getFrameworkById(id) || null;
  }

  try {
    const framework = await apiClient.get(`/api/atlas/frameworks/${id}`);
    return framework;
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getFrameworkById(id) || null;
  }
}

/**
 * Fetch personas from API or fall back to JSON
 * @returns {Promise<import('./types').Persona[]>}
 */
export async function fetchPersonas() {
  if (!isApiEnabled()) {
    return contentLoader.getPersonas();
  }

  try {
    const response = await apiClient.get('/api/atlas/personas');
    return response.personas || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getPersonas();
  }
}

/**
 * Fetch a single persona by ID
 * @param {string} id - Persona ID
 * @returns {Promise<import('./types').Persona|null>}
 */
export async function fetchPersona(id) {
  if (!isApiEnabled()) {
    return contentLoader.getPersonaById(id) || null;
  }

  try {
    const persona = await apiClient.get(`/api/atlas/personas/${id}`);
    return persona;
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getPersonaById(id) || null;
  }
}

/**
 * Fetch scenarios from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.type] - Filter by type
 * @param {string} [filters.difficulty] - Filter by difficulty
 * @returns {Promise<Object>}
 */
export async function fetchScenarios(filters = {}) {
  if (!isApiEnabled()) {
    return {
      useCases: contentLoader.getUseCases(),
      timelines: contentLoader.getTimelines(),
      objections: contentLoader.getObjections()
    };
  }

  try {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.difficulty) params.append('difficulty', filters.difficulty);

    const queryString = params.toString();
    const url = `/api/atlas/scenarios${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return {
      scenarios: response.scenarios || [],
      // Keep backward compatibility with existing scenario structure
      useCases: response.scenarios?.filter(s => s.type === 'use_case') || [],
      timelines: response.scenarios?.filter(s => s.type === 'transformation') || [],
      objections: []
    };
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return {
      useCases: contentLoader.getUseCases(),
      timelines: contentLoader.getTimelines(),
      objections: contentLoader.getObjections()
    };
  }
}

/**
 * Fetch edges (product relationships) from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.product] - Filter by product (source or target)
 * @param {string} [filters.type] - Filter by edge type
 * @returns {Promise<import('./types').Edge[]>}
 */
export async function fetchEdges(filters = {}) {
  if (!isApiEnabled()) {
    let edges = contentLoader.getEdges();
    if (filters.product) {
      edges = edges.filter(e =>
        e.source === filters.product || e.target === filters.product
      );
    }
    if (filters.type) {
      edges = edges.filter(e => e.type === filters.type);
    }
    return edges;
  }

  try {
    const params = new URLSearchParams();
    if (filters.product) params.append('product', filters.product);
    if (filters.type) params.append('type', filters.type);

    const queryString = params.toString();
    const url = `/api/atlas/edges${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.edges || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getEdges();
  }
}

/**
 * Fetch 3D model positions from API or fall back to JSON
 * @returns {Promise<import('./types').NodePosition[]>}
 */
export async function fetchModel3dPositions() {
  if (!isApiEnabled()) {
    return contentLoader.getNodePositions();
  }

  try {
    const response = await apiClient.get('/api/atlas/model3d');
    return response.positions || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getNodePositions();
  }
}

/**
 * Fetch glossary terms from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.category] - Filter by category
 * @param {string} [filters.search] - Search query
 * @returns {Promise<import('./types').GlossaryTerm[]>}
 */
export async function fetchGlossary(filters = {}) {
  if (!isApiEnabled()) {
    if (filters.search) {
      return contentLoader.searchGlossary(filters.search);
    }
    return contentLoader.getGlossaryTerms();
  }

  try {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `/api/atlas/glossary${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.terms || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    if (filters.search) {
      return contentLoader.searchGlossary(filters.search);
    }
    return contentLoader.getGlossaryTerms();
  }
}

/**
 * Fetch diagrams from API or fall back to JSON
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.category] - Filter by category
 * @param {string} [filters.type] - Filter by diagram type
 * @returns {Promise<import('./types').Diagram[]>}
 */
export async function fetchDiagrams(filters = {}) {
  if (!isApiEnabled()) {
    let diagrams = contentLoader.getDiagrams();
    if (filters.category) {
      diagrams = diagrams.filter(d => d.category === filters.category);
    }
    if (filters.type) {
      diagrams = diagrams.filter(d => d.diagramType === filters.type);
    }
    return diagrams;
  }

  try {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.type) params.append('type', filters.type);

    const queryString = params.toString();
    const url = `/api/atlas/diagrams${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return response.diagrams || [];
  } catch (err) {
    console.warn('Atlas API unavailable, falling back to JSON:', err.message);
    return contentLoader.getDiagrams();
  }
}

/**
 * Check Atlas API health
 * @returns {Promise<{status: string, mode: string, tables: Object}>}
 */
export async function checkApiHealth() {
  try {
    const response = await apiClient.get('/api/atlas/health');
    return response;
  } catch (err) {
    return {
      status: 'unavailable',
      mode: 'fallback_json',
      error: err.message
    };
  }
}

/**
 * Enable or disable API mode (for development)
 * @param {boolean} enabled - Whether to enable API mode
 */
export function setApiEnabled(enabled) {
  localStorage.setItem('atlas_api_enabled', enabled ? 'true' : 'false');
}

/**
 * Check if API mode is currently enabled
 * @returns {boolean}
 */
export function getApiEnabled() {
  return isApiEnabled();
}

export default {
  fetchProducts,
  fetchProduct,
  fetchCapabilities,
  fetchFrameworks,
  fetchFramework,
  fetchPersonas,
  fetchPersona,
  fetchScenarios,
  fetchEdges,
  fetchModel3dPositions,
  fetchGlossary,
  fetchDiagrams,
  checkApiHealth,
  setApiEnabled,
  getApiEnabled
};
